import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { signUp, confirmSignUp, resendSignUpCode } from 'aws-amplify/auth';
import { toast } from 'react-toastify';
import { config } from '../config';

interface LocationState {
  email?: string;
  needsVerification?: boolean;
}

const Register: React.FC = () => {
  const location = useLocation();
  const locationState = location.state as LocationState;
  
  const [step, setStep] = useState<'register' | 'confirm'>(
    locationState?.needsVerification ? 'confirm' : 'register'
  );
  const [formData, setFormData] = useState({
    email: locationState?.email || '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
    address: '',
    userType: 'parent'
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Check email if it's auto-populated by browser on mount
  useEffect(() => {
    // Check email after a short delay to allow browser auto-fill to complete
    const checkAutoFilledEmail = setTimeout(() => {
      if (formData.email && formData.email.includes('@')) {
        // Only check if email looks valid
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (emailRegex.test(formData.email)) {
          checkEmailExists(formData.email);
        }
      }
    }, 1000); // Wait 1 second for browser auto-fill to complete

    return () => clearTimeout(checkAutoFilledEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Also check when email changes (handles auto-fill after initial mount or programmatic changes)
  useEffect(() => {
    // Skip if email is empty or invalid format
    if (!formData.email || !formData.email.includes('@')) {
      setEmailExists(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setEmailExists(false);
      return;
    }

    // Don't check immediately on every change - wait a bit for auto-fill to complete
    const timeoutId = setTimeout(() => {
      checkEmailExists(formData.email);
    }, 1000); // Wait 1 second after email changes (longer than auto-fill typically takes)

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.email]); // Re-run when email changes

  const checkEmailExists = async (email: string) => {
    if (!email || !email.includes('@')) {
      setEmailExists(false);
      return;
    }

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailExists(false);
      return;
    }

    setCheckingEmail(true);
    try {
      const response = await fetch(`${config.BACKEND_URL}/api/test-user-by-email/${encodeURIComponent(email)}`);
      const data = await response.json();
      
      if (data.success && data.user) {
        setEmailExists(true);
      } else {
        setEmailExists(false);
      }
    } catch (error) {
      console.error('Error checking email:', error);
      // On error, don't show email exists message (fail silently)
      setEmailExists(false);
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    setFormData({
      ...formData,
      email: value
    });

    // Reset email exists state when user starts typing
    setEmailExists(false);
  };

  const handleEmailBlur = () => {
    // Check when user leaves the email field
    if (formData.email) {
      // Reset the state first to clear any previous message
      setEmailExists(false);
      // Then check
      checkEmailExists(formData.email);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    // Handle email separately for real-time validation
    if (e.target.name === 'email') {
      handleEmailChange(e as React.ChangeEvent<HTMLInputElement>);
      return;
    }

    let value = e.target.value;
    
    // For phone field, only allow digits and limit to 10 digits
    if (e.target.name === 'phone') {
      value = value.replace(/\D/g, ''); // Remove all non-digits
      if (value.length > 10) {
        value = value.slice(0, 10); // Limit to 10 digits
      }
    }
    
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const validateForm = () => {
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return false;
    }
    // Password complexity check
    const hasUpperCase = /[A-Z]/.test(formData.password);
    const hasLowerCase = /[a-z]/.test(formData.password);
    const hasNumbers = /\d/.test(formData.password);
    // Include underscore and other common special characters
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/~`]/.test(formData.password);
    
    if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChar) {
      toast.error('Password must contain uppercase, lowercase, number, and special character');
      return false;
    }
    
    // Phone number validation (10-digit Canadian format)
    const phoneDigits = formData.phone.replace(/\D/g, ''); // Remove all non-digits
    if (phoneDigits.length !== 10) {
      toast.error('Phone must be a 10-digit Canadian number (e.g., 2501234567)');
      return false;
    }
    
    // Address is now optional, so no validation needed
    
    return true;
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);

    try {
      // Build user attributes - Cognito requires address attribute, so send empty string if not provided
      const addressValue = formData.address && formData.address.trim().length > 0 
        ? formData.address.trim() 
        : 'Not provided';
      
      // Format phone number: remove all non-digits and ensure it's 10 digits
      const phoneDigits = formData.phone.replace(/\D/g, '');
      const formattedPhone = phoneDigits.length === 10 ? `+1${phoneDigits}` : formData.phone; // Add +1 for Cognito storage
      
      const userAttributes: any = {
        email: formData.email,
        given_name: formData.firstName,
        family_name: formData.lastName,
        name: `${formData.firstName} ${formData.lastName}`,
        phone_number: formattedPhone,
        address: addressValue
      };
      
      // Debug logging
      console.log('🔍 Registration attempt:', {
        email: formData.email,
        hasAddress: !!formData.address,
        addressValue: addressValue,
        userAttributes: userAttributes
      });
      
      await signUp({
        username: formData.email,
        password: formData.password,
        options: {
          userAttributes: userAttributes,
          // Note: custom:user_type is not included because it's not configured in Cognito User Pool
          // User type will be set in the database after registration via backend API
          autoSignIn: false
        }
      });

      toast.success('Registration successful! Please check your email for verification code.');
      setStep('confirm');
    } catch (error: any) {
      console.error('Registration error:', error);
      if (error.name === 'UsernameExistsException') {
        toast.error('An account with this email already exists');
      } else if (error.name === 'InvalidPasswordException') {
        toast.error('Password does not meet requirements');
      } else if (error.name === 'InvalidParameterException') {
        // Check if it's specifically about address attribute
        if (error.message && error.message.includes('address')) {
          toast.error('Address field issue. Please try again or contact support.');
        } else {
          toast.error('Invalid input. Please check all fields, especially phone format (10-digit Canadian number)');
        }
        console.error('InvalidParameterException details:', error);
      } else {
        toast.error(error.message || 'Registration failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setLoading(true);

    try {
      await confirmSignUp({
        username: formData.email,
        confirmationCode: verificationCode
      });

      // Set user_type in database after successful email confirmation
      try {
        const response = await fetch(`${config.BACKEND_URL}/api/users/set-user-type`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: formData.email,
            userType: formData.userType
          })
        });
        
        if (response.ok) {
          const result = await response.json();
          console.log('User type set:', result);
        } else {
          console.warn('Failed to set user type, but registration was successful');
        }
      } catch (err) {
        // Non-critical error - user can still sign in
        console.warn('Could not set user type:', err);
      }

      toast.success('Email verified successfully! You can now sign in.');
      navigate('/login');
    } catch (error: any) {
      console.error('Verification error:', error);
      if (error.name === 'CodeMismatchException') {
        toast.error('Invalid verification code. Please try again.');
      } else if (error.name === 'ExpiredCodeException') {
        toast.error('Verification code expired. Please request a new one.');
      } else if (error.name === 'NotAuthorizedException') {
        toast.error('User already confirmed. Please sign in.');
        navigate('/login');
      } else {
        toast.error(error.message || 'Verification failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    if (resendCooldown > 0) return;
    
    try {
      setLoading(true);
      await resendSignUpCode({ username: formData.email });
      toast.success('Verification code resent! Please check your email.');
      setResendCooldown(60); // 60 second cooldown
    } catch (error: any) {
      console.error('Resend error:', error);
      toast.error('Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'confirm') {
    return (
      <div className="login-page">
        {/* OARA Header */}
        <div className="login-header">
          <div className="login-header-content">
            <div className="login-logo-section">
              <img 
                src="https://oarayouthinnovationlab.ca/wp-content/uploads/2025/08/cropped-Logo-1-83x75-1.webp" 
                alt="OARA Logo" 
                className="oara-logo"
              />
              <div className="login-title-section">
                <h1>OARA YOUTH INNOVATION LAB</h1>
                <p className="login-subtitle">Learning Management Portal</p>
              </div>
            </div>
            <div className="login-contact-info">
              <p><strong>Downtown Vernon Makerspace</strong></p>
              <p>3125 31 Ave, Vernon, BC V1T 2H1</p>
              <p>Phone: <a href="tel:2367950470">(236) 795-0470</a></p>
              <p>Email: <a href="mailto:oarayouthinnovationlab@gmail.com">oarayouthinnovationlab@gmail.com</a></p>
            </div>
          </div>
        </div>
        <div className="container">
        <div className="card">
          <h2>Verify Your Email</h2>
          <p style={{ color: '#666', marginBottom: '1.5rem' }}>
            We've sent a 6-digit verification code to <strong>{formData.email}</strong>
          </p>
          
          <form onSubmit={handleConfirmSignUp}>
            <div className="form-group">
              <label htmlFor="code">Verification Code</label>
              <input
                type="text"
                id="code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                required
                placeholder="Enter 6-digit code"
                maxLength={6}
                style={{ fontSize: '1.2rem', letterSpacing: '0.5rem', textAlign: 'center' }}
              />
            </div>

            <button type="submit" className="btn" disabled={loading || verificationCode.length !== 6}>
              {loading ? 'Verifying...' : 'Verify Email'}
            </button>
          </form>

          <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
            <button 
              onClick={resendCode} 
              className="link-button"
              disabled={loading || resendCooldown > 0}
              style={{ fontSize: '0.9rem' }}
            >
              {resendCooldown > 0 
                ? `Resend Code (${resendCooldown}s)` 
                : 'Resend Verification Code'}
            </button>
          </div>

          <div style={{ marginTop: '1rem', textAlign: 'center' }}>
            <Link to="/login" style={{ color: '#666', fontSize: '0.9rem' }}>
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
  }

  return (
    <div className="login-page">
      {/* OARA Header */}
      <div className="login-header">
        <div className="login-header-content">
          <div className="login-logo-section">
            <img 
              src="https://oarayouthinnovationlab.ca/wp-content/uploads/2025/08/cropped-Logo-1-83x75-1.webp" 
              alt="OARA Logo" 
              className="oara-logo"
            />
            <div className="login-title-section">
              <h1>OARA YOUTH INNOVATION LAB</h1>
              <p className="login-subtitle">Learning Management Portal</p>
            </div>
          </div>
          <div className="login-contact-info">
            <p><strong>Downtown Vernon Makerspace</strong></p>
            <p>3125 31 Ave, Vernon, BC V1T 2H1</p>
            <p>Phone: <a href="tel:2367950470">(236) 795-0470</a></p>
            <p>Email: <a href="mailto:oarayouthinnovationlab@gmail.com">oarayouthinnovationlab@gmail.com</a></p>
          </div>
        </div>
      </div>
      <div className="container">
        <div className="card" style={{ maxWidth: '600px' }}>
        <h2>Create Your Account</h2>
        <p style={{ color: '#666', marginBottom: '1.5rem' }}>
          Fill in your details to get started
        </p>
        
        <form onSubmit={handleRegister}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">Parent First Name *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                placeholder="John"
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Parent Last Name *</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                placeholder="Doe"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleEmailBlur}
              onInput={(e) => {
                // Handle browser auto-fill which may not trigger onChange
                const target = e.target as HTMLInputElement;
                if (target.value !== formData.email) {
                  handleEmailChange({ target } as React.ChangeEvent<HTMLInputElement>);
                  // Check after a delay to allow auto-fill to complete
                  setTimeout(() => {
                    if (target.value) {
                      checkEmailExists(target.value);
                    }
                  }, 1000);
                }
              }}
              required
              placeholder="john.doe@example.com"
              style={{
                borderColor: emailExists ? '#dc3545' : undefined,
                borderWidth: emailExists ? '2px' : undefined
              }}
            />
            {checkingEmail && (
              <small style={{ color: '#666', fontStyle: 'italic', display: 'block', marginTop: '4px' }}>
                Checking...
              </small>
            )}
            {emailExists && !checkingEmail && (
              <small style={{ color: '#dc3545', display: 'block', marginTop: '4px', fontWeight: '500' }}>
                ⚠️ An account with this email already exists. Use{' '}
                <Link 
                  to="/login" 
                  style={{ 
                    color: '#667eea', 
                    textDecoration: 'underline',
                    fontWeight: '600'
                  }}
                >
                  forgot password
                </Link>
                {' '}to reset password.
              </small>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number *</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              placeholder="2501234567"
              maxLength={10}
            />
            <small>10-digit Canadian phone number (e.g., 2501234567)</small>
          </div>

          <div className="form-group">
            <label htmlFor="address">Address</label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="123 Main St, City, State, ZIP"
            />
          </div>

          <div className="form-group">
            <label htmlFor="userType">I am a *</label>
            <select
              id="userType"
              name="userType"
              value={formData.userType}
              onChange={handleChange}
              required
            >
              <option value="parent">Parent</option>
            </select>
            <small>Note: Only parent accounts can be created through self-registration. Admin, teacher, and student accounts must be created by administrators.</small>
          </div>

          <div className="form-row">
            <div className="form-group" style={{ position: 'relative' }}>
              <label htmlFor="password">Password *</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="Min 8 characters"
                  style={{ paddingRight: '40px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#666',
                    fontSize: '18px'
                  }}
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <div className="form-group" style={{ position: 'relative' }}>
              <label htmlFor="confirmPassword">Confirm Password *</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  placeholder="Re-enter password"
                  style={{ paddingRight: '40px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#666',
                    fontSize: '18px'
                  }}
                  title={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>
          </div>

          <small style={{ display: 'block', marginBottom: '1rem', color: '#666' }}>
            Password must contain: uppercase, lowercase, number, and special character (e.g., !@#$%^&*_+-=)
          </small>

          <button type="submit" className="btn" disabled={loading}>
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
          <p style={{ color: '#666' }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: '#007bff', textDecoration: 'none', fontWeight: '500' }}>
              Sign In
            </Link>
          </p>
        </div>
      </div>
      </div>
      
      {/* OARA Footer */}
      <div className="login-page-footer">
        <div className="login-footer-content">
          <h3>OARA Youth Innovation Lab</h3>
          <div className="login-footer-details">
            <p><strong>Downtown Vernon Makerspace</strong></p>
            <p>3125 31 Ave, Vernon, BC V1T 2H1</p>
            <p>Phone: <a href="tel:2367950470">(236) 795-0470</a></p>
            <p>Email: <a href="mailto:oarayouthinnovationlab@gmail.com">oarayouthinnovationlab@gmail.com</a></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;